import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared } from '@krp/client-runtime'

import type { ReceiptsFilter, ReceiptsIntentState, ReceiptsTab, ReceiptsView, SyncFailure } from './types'

/**
 * Намерение человека в журнале чеков по каждому ресурсу: форма отбора, применённый
 * отбор и цепочка курсоров страниц. Самих чеков здесь НЕТ — они в кэше RTK Query
 * (R01). Курсор — серверный ключ среза журнала, а не секрет и не токен; он
 * переживает F5 (session), пока жив на сервере, а просроченный честно отвечает
 * 410 «начните выдачу заново».
 */
export const EMPTY_RECEIPTS_FILTER: ReceiptsFilter = {
  registerId: '',
  shop: '',
  cashier: '',
  shiftNumber: '',
  dateFrom: '',
  dateTo: '',
  search: '',
  kind: '',
}

export const EMPTY_RECEIPTS_VIEW: ReceiptsView = {
  draft: EMPTY_RECEIPTS_FILTER,
  applied: EMPTY_RECEIPTS_FILTER,
  cursors: [],
  page: 0,
}

/** Сколько ресурсов и страниц помнит намерение: брошенное не копится. */
export const MAX_REMEMBERED_RESOURCES = 8
export const MAX_REMEMBERED_PAGES = 200
/** Сколько свёрнутых узлов дерева помнит намерение: брошенное не копится. */
export const MAX_COLLAPSED_NODES = 200

const initialState: ReceiptsIntentState = { byResource: {}, syncError: {}, syncing: {} }

function viewOf(state: ReceiptsIntentState, resourceId: string): ReceiptsView {
  return state.byResource[resourceId] ?? EMPTY_RECEIPTS_VIEW
}

function remember(state: ReceiptsIntentState, resourceId: string, view: ReceiptsView) {
  state.byResource[resourceId] = view
  const ids = Object.keys(state.byResource)
  // Забываем самые давние ресурсы, но не только что изменённый.
  for (const id of ids.slice(0, Math.max(0, ids.length - MAX_REMEMBERED_RESOURCES))) {
    if (id !== resourceId) delete state.byResource[id]
  }
}

const receiptsSlice = createSlice({
  name: 'aqsi/receipts',
  initialState,
  reducers: {
    /** Переход из РМК заменяет прежние отборы кассиром, ККТ и днём активной работы. */
    rmkReceiptsOpened(state, action: PayloadAction<{ resourceId: string; registerId: string; cashier: string; day: string }>) {
      const { resourceId, registerId, cashier, day } = action.payload
      const filter = { ...EMPTY_RECEIPTS_FILTER, registerId, cashier, dateFrom: day, dateTo: day }
      remember(state, resourceId, { ...EMPTY_RECEIPTS_VIEW, draft: filter, applied: filter, tab: 'journal' })
    },
    draftChanged(state, action: PayloadAction<{ resourceId: string; patch: Partial<ReceiptsFilter> }>) {
      const { resourceId, patch } = action.payload
      const view = viewOf(state, resourceId)
      // Смена кассы обнуляет смену: номер смены принадлежит кассе.
      const reset = patch.registerId !== undefined && patch.registerId !== view.draft.registerId ? { shiftNumber: '' } : {}
      remember(state, resourceId, { ...view, draft: { ...view.draft, ...patch, ...reset } })
    },
    /**
     * Отбор из дерева, дней недели и чипов: применяется сразу и целиком (и форма, и применённое), выдача с начала —
     * курсоры прежнего отбора не годятся (409). Поиск, набранный и не применённый, остаётся в форме.
     */
    quickFilterChanged(state, action: PayloadAction<{ resourceId: string; patch: Partial<ReceiptsFilter> }>) {
      const { resourceId, patch } = action.payload
      const view = viewOf(state, resourceId)
      remember(state, resourceId, {
        ...view,
        draft: { ...view.draft, ...patch },
        applied: { ...view.applied, ...patch },
        cursors: [],
        page: 0,
      })
    },
    /** Свернуть или развернуть узел дерева. */
    treeToggled(state, action: PayloadAction<{ resourceId: string; nodeId: string }>) {
      const { resourceId, nodeId } = action.payload
      const view = viewOf(state, resourceId)
      const collapsed = view.collapsed ?? []
      remember(state, resourceId, {
        ...view,
        collapsed: collapsed.includes(nodeId)
          ? collapsed.filter((id) => id !== nodeId)
          : [...collapsed, nodeId].slice(-MAX_COLLAPSED_NODES),
      })
    },
    /** Применить форму: новый отбор — новая выдача, курсоры старого отбора не годятся (409). */
    filterApplied(state, action: PayloadAction<{ resourceId: string }>) {
      const view = viewOf(state, action.payload.resourceId)
      remember(state, action.payload.resourceId, { ...view, applied: view.draft, cursors: [], page: 0 })
    },
    filterReset(state, action: PayloadAction<{ resourceId: string }>) {
      remember(state, action.payload.resourceId, EMPTY_RECEIPTS_VIEW)
    },
    pageAdvanced(state, action: PayloadAction<{ resourceId: string; cursor: string }>) {
      const { resourceId, cursor } = action.payload
      const view = viewOf(state, resourceId)
      if (view.page >= MAX_REMEMBERED_PAGES) return
      remember(state, resourceId, { ...view, cursors: [...view.cursors.slice(0, view.page), cursor], page: view.page + 1 })
    },
    pageReturned(state, action: PayloadAction<{ resourceId: string }>) {
      const view = viewOf(state, action.payload.resourceId)
      if (view.page === 0) return
      remember(state, action.payload.resourceId, { ...view, page: view.page - 1 })
    },
    /** Начать выдачу заново: тот же отбор, первая страница (просроченный курсор). */
    listRestarted(state, action: PayloadAction<{ resourceId: string }>) {
      const view = viewOf(state, action.payload.resourceId)
      remember(state, action.payload.resourceId, { ...view, cursors: [], page: 0 })
    },
    /** Сменить вкладку. */
    receiptsTabChosen(state, action: PayloadAction<{ resourceId: string; tab: ReceiptsTab }>) {
      const { resourceId, tab } = action.payload
      remember(state, resourceId, { ...viewOf(state, resourceId), tab })
    },
    /**
     * «Чеки этой смены» из раздела «Смены»: отбор журнала — только касса и номер смены,
     * сразу применён; выдача с начала (курсоры прежнего отбора не годятся).
     */
    shiftChosen(state, action: PayloadAction<{ resourceId: string; registerId: string; shiftNumber: string }>) {
      const { resourceId, registerId, shiftNumber } = action.payload
      const view = viewOf(state, resourceId)
      const filter = { ...EMPTY_RECEIPTS_FILTER, registerId, shiftNumber }
      remember(state, resourceId, { ...view, draft: filter, applied: filter, cursors: [], page: 0, tab: 'journal' })
    },
    viewsRestored(state, action: PayloadAction<Readonly<Record<string, ReceiptsView>>>) {
      state.byResource = { ...action.payload }
    },
    viewRestored(state, action: PayloadAction<{ resourceId: string; view: ReceiptsView }>) {
      remember(state, action.payload.resourceId, action.payload.view)
    },
    journalSyncFailed(state, action: PayloadAction<{ resourceId: string; failure: SyncFailure }>) {
      state.syncError[action.payload.resourceId] = action.payload.failure
    },
    journalSyncStarted(state, action: PayloadAction<{ resourceId: string }>) {
      state.syncing[action.payload.resourceId] = true
    },
    journalSyncFinished(state, action: PayloadAction<{ resourceId: string }>) {
      delete state.syncing[action.payload.resourceId]
    },
    journalSyncCleared(state, action: PayloadAction<{ resourceId: string }>) {
      delete state.syncError[action.payload.resourceId]
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
    builder.addCase(resourceScopeCleared, (state, action) => {
      delete state.byResource[action.payload.resourceId]
      delete state.syncError[action.payload.resourceId]
      delete state.syncing[action.payload.resourceId]
    })
  },
})

export const {
  rmkReceiptsOpened,
  draftChanged,
  quickFilterChanged,
  treeToggled,
  filterApplied,
  filterReset,
  pageAdvanced,
  pageReturned,
  listRestarted,
  receiptsTabChosen,
  shiftChosen,
  viewsRestored,
  viewRestored,
  journalSyncFailed,
  journalSyncStarted,
  journalSyncFinished,
  journalSyncCleared,
} = receiptsSlice.actions
export const receiptsReducer = receiptsSlice.reducer

const MAX_TEXT = 200
const DATE = /^\d{4}-\d{2}-\d{2}$/

function textField(value: unknown): string | undefined {
  return typeof value === 'string' && value.length <= MAX_TEXT ? value : undefined
}

function dateField(value: unknown): string | undefined {
  const text = textField(value)
  return text !== undefined && (text === '' || DATE.test(text)) ? text : undefined
}

/** Проверка сохранённого отбора (D-072): битое — `undefined`, слайс берёт пустой. */
export function decodeReceiptsFilter(raw: unknown): ReceiptsFilter | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined
  const value = raw as Record<string, unknown>
  const registerId = textField(value.registerId)
  const shop = textField(value.shop)
  const cashier = textField(value.cashier)
  const shiftNumber = textField(value.shiftNumber)
  const dateFrom = dateField(value.dateFrom)
  const dateTo = dateField(value.dateTo)
  const search = textField(value.search)
  const kind = value.kind === undefined ? '' : value.kind
  if (kind !== '' && kind !== 'sale' && kind !== 'refund' && kind !== 'unconfirmed') return undefined
  if (
    registerId === undefined ||
    shop === undefined ||
    cashier === undefined ||
    shiftNumber === undefined ||
    dateFrom === undefined ||
    dateTo === undefined ||
    search === undefined
  ) {
    return undefined
  }
  return { registerId, shop, cashier, shiftNumber, dateFrom, dateTo, search, kind }
}

export function decodeReceiptsView(raw: unknown): ReceiptsView | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined
  const value = raw as Record<string, unknown>
  const draft = decodeReceiptsFilter(value.draft)
  const applied = decodeReceiptsFilter(value.applied)
  const cursors = value.cursors
  const page = value.page
  if (draft === undefined || applied === undefined) return undefined
  if (!Array.isArray(cursors) || cursors.length > MAX_REMEMBERED_PAGES) return undefined
  if (!cursors.every((cursor): cursor is string => typeof cursor === 'string' && cursor !== '' && cursor.length <= 4096)) {
    return undefined
  }
  if (typeof page !== 'number' || !Number.isInteger(page) || page < 0 || page > cursors.length) return undefined
  // Вкладка необязательна: записи, сохранённые до вкладок, читаются как журнал. Вкладки «Мои смены» больше нет (смены — раздел
  // «Смены», решение владельца 26.10.2026): запись с ней читается как журнал, её период «Моих смен» (`shiftsPeriod`) не нужен.
  const stored = value.tab
  if (stored !== undefined && stored !== 'journal' && stored !== 'my-shifts' && stored !== 'attention' && stored !== 'parked') return undefined
  const tab: ReceiptsTab | undefined = stored === 'my-shifts' ? 'journal' : stored
  // Свёрнутые узлы дерева — необязательны: записи до дерева читаются как «всё развёрнуто».
  const collapsed = value.collapsed
  if (
    collapsed !== undefined &&
    (!Array.isArray(collapsed) ||
      collapsed.length > MAX_COLLAPSED_NODES ||
      !collapsed.every((id): id is string => typeof id === 'string' && id !== '' && id.length <= MAX_TEXT + 20))
  ) {
    return undefined
  }
  return {
    draft,
    applied,
    cursors,
    page,
    ...(tab !== undefined ? { tab } : {}),
    ...(collapsed !== undefined ? { collapsed } : {}),
  }
}

export function decodeReceiptsViews(raw: unknown): Readonly<Record<string, ReceiptsView>> | undefined {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return undefined
  const entries = Object.entries(raw)
  if (entries.length > MAX_REMEMBERED_RESOURCES) return undefined
  const result: Record<string, ReceiptsView> = {}
  for (const [resourceId, view] of entries) {
    const decoded = decodeReceiptsView(view)
    if (decoded === undefined || resourceId === '') return undefined
    result[resourceId] = decoded
  }
  return result
}
